import { type PropertiesContext } from '$lib/context';
import { goto } from '$app/navigation';
import { get } from 'svelte/store';
import { page } from '$app/stores';
import { searchFilter } from '$lib/stores';
import type { SupportedLocale } from '$lib/i18n/initI18n';
import { browser } from '$app/environment';

export type Filter = { [key: string]: string[] };

export function createFilter(properties: PropertiesContext, preselectedFilter?: Filter): Filter {
    const filter = Object.fromEntries(Object.keys(properties).map((key) => [key, []])) as {
        [key: string]: string[];
    };

    if (preselectedFilter) {
        for (const [key, values] of Object.entries(preselectedFilter)) {
            for (const id of values) {
                if (!filter[key]) continue;

                filter[key].push(id);
            }
        }
    }

    return filter;
}

export function buildFilter(e: Event, property: string, filter: Filter) {
    const target = e.target as HTMLInputElement;

    if (target.checked) {
        filter[property].push(target.value);
    } else {
        filter[property] = filter[property].filter((value) => value !== target.value);
    }

    return (
        Object.entries(filter)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([key, option]) => option.length > 0)
            .map(([key, values]) => `${key}:[${values.join(',')}]`)
            .join(' && ')
    );
}

export async function setSearchFilter(
    locale: SupportedLocale,
    filterKey: string,
    filterValue: string,
    enabled: boolean
) {
    const filter = { ...get(searchFilter) };

    if (enabled) {
        filter[filterKey] ??= [];
        filter[filterKey].push(filterValue);
    } else {
        filter[filterKey] = filter[filterKey].filter((value) => value !== filterValue);

        if (filter[filterKey].length === 0) {
            delete filter[filterKey];
        }
    }

    await updateSearch(locale, {
        filter
    });
}

export async function updateSearch(locale: SupportedLocale, { q, filter }: { q?: string; filter?: Filter }) {
    if (!browser) return;

    const searchParams = new URLSearchParams(get(page).url.searchParams);

    if (typeof q === 'string') {
        if (q) {
            searchParams.set('q', q);
        } else {
            searchParams.delete('q');
        }
    }

    if (typeof filter === 'object') {
        if (Object.keys(filter).length > 0) {
            searchParams.set('filter', JSON.stringify(filter));
        } else {
            searchParams.delete('filter');
        }
    }

    const searchString = searchParams.toString();
    await goto(`/${locale}/search${searchString ? '?' + searchString : ''}`, {
        replaceState: true,
        keepFocus: true,
        noScroll: true
    });
}

export function buildFilterURL(filterObj: Filter) {
    const filterArray = Object.entries(filterObj).filter(([key, values]) => values.length > 0);

    if (filterArray.length < 1) {
        return undefined;
    }

    filterObj = Object.fromEntries(filterArray);

    return encodeURIComponent(JSON.stringify(filterObj).replace(/\+/g, ' '));
}
